import IBaseModel from "../base/base-model";
import type { ITripStartCheck } from "./start-check";

export enum TripStatus {
  PENDING = 1,
  ACTIVE = 2,
  COMPLETED = 3,
  CANCELLED = 4,
}

export enum PaymentStatus {
  ACCEPTED = 1, // مدفوعة ✅
  PENDING = 2, // معلقة ⏳
  REJECTED = 3, // مرفوضة ❌
}

// Order payment type enum
export enum OrderTypePayment {
  CASH = "CASH",
  ELECTRONIC = "ELECTRONIC",
  WALLET = "WALLET",
}

// Store in trip with its orders
export interface ITripStore {
  id: number;
  name: string;
  phone?: string;
  address?: string;
  logo?: string | null;
  location?: {
    type?: string;
    coordinates?: number[];
    lat?: number;
    lng?: number;
  };
  productsCount?: number;
  productsTotalValue?: number;
  orders?: {
    id: number;
    number: string;
  }[];
}

export interface ITripOrder {
  id: number;
  number: string;
  status: string | number;
  pivot: {
    sequence: number;
    distanceToNext: number;
  };
  // Client info - API returns clientName as flat string for trip orders
  clientName?: string;
  clientMobile?: string;
  clientAddress?: string;
  client?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    mobile?: string;
  };
  // Product info
  productsCount?: number;
  productsTotalPrice?: number;
  totalPrice?: number;
  // Payment info
  orderTypePayment?: OrderTypePayment | string;
  finalPrice?: number;
  totalPriceOnClient?: number;
  paymentReceivedBy?: number | null;
  paymentReceivedAt?: string | null;
  // Location for map
  location?: {
    type?: string;
    coordinates?: number[];
    lat?: number;
    lng?: number;
  };
}

// Order distance breakdown in cost calculation
export interface IOrderDistanceBreakdown {
  order_id: number;
  stores_count?: number;
  route_distance: number; // Distance from store to order (one-way)
  return_distance: number; // Distance from order back to store
  round_trip_distance: number; // Total round trip distance
}

// Cost breakdown structure from API
export interface ICostBreakdown {
  base_cost?: number;
  bonus_cost?: number;
  orders_count?: number;
  bonus_enabled?: boolean;
  bonus_per_order?: number;
  order_distances?: IOrderDistanceBreakdown[];
  calculation_type?: string;
  additional_orders?: number;
  cost_per_kilometer?: number;
  base_cost_with_return?: number;
  total_trip_distance?: number;
  total_trip_distance_with_return?: number;
}

// Status history entry for tracking trip status changes
export interface ITripStatusHistory {
  id: number;
  /** Previous status (null for initial creation) */
  fromStatus: TripStatus | null;
  /** New status after change */
  toStatus: TripStatus;
  /** User who made the change */
  changedBy?: {
    id: number;
    name?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  /** Reason for status change (especially for cancellation) */
  changeReason?: string | null;
  /** Additional context-specific data */
  metadata?: {
    driver_id?: number;
    orders_count?: number;
    total_distance?: number;
    duration_minutes?: number;
    actual_duration_minutes?: number;
    completed_orders?: number;
    failed_orders?: number;
    cancellation_type?: string;
    [key: string]: any;
  } | null;
  /** Timestamp of the status change */
  createdAt: string;
}

// ========================================
// New Route System Interfaces
// ========================================

/** Polyline segment from mapPolylines */
export interface IPolylineSegment {
  index: number;
  polyline: string;
  distanceMeters: number;
  durationSeconds: number;
}

/** MapPolylines container */
export interface IMapPolylines {
  segments: IPolylineSegment[];
  source: string; // "hybrid_shortest", "route_optimization", "legacy_directions", "legacy_overview"
  totalSegments: number;
}

/** GeoJSON Point location format from API */
export interface IGeoJsonLocation {
  type: "Point";
  coordinates: [number, number]; // [longitude, latitude] - NOT [lat, lng]!
}

/** Route point for activeRoute - NOTE: No names, use stores/orders for names */
export interface IRoutePoint {
  type: "store" | "delivery";
  store_id?: number; // Only for type="store"
  order_id: number;
  /** ⚠️ GeoJSON format: coordinates = [lng, lat] */
  location: IGeoJsonLocation;
  sequence: number;
}

/** Order sequence entry */
export interface IOrderSequence {
  order_id: number;
  sequence: number;
}

/** Distance entry between route points */
export interface IRouteDistance {
  from: string; // "origin", "store_45", "delivery_order_789"
  to: string;
  distance: number;
}

/** Active route structure */
export interface IActiveRoute {
  route_points: IRoutePoint[];
  order_sequences: IOrderSequence[];
  distances: IRouteDistance[];
  total_distance: number;
}

/** Full route data for comparison (includes polylines for map rendering) */
export interface IComparisonRoute {
  polylines: IPolylineSegment[];
  route_points: IRoutePoint[];
  distances: IRouteDistance[];
  total_distance: number;
}

/** Strategy details in route comparison (NEW FORMAT) */
export interface ITripStrategy {
  id: number;
  name: string;
  nameAr: string;
  totalDistance: number;
  routePointsCount: number;
  isActive: boolean;
  /** Polylines for rendering this strategy's route on the map */
  polylines?: IPolylineSegment[];
}

/** Strategy ranking entry (NEW FORMAT) */
export interface IStrategyRanking {
  key: string;
  id: number;
  name: string;
  nameAr?: string;
  totalDistance?: number;
  rank: number;
}

/** Distance savings for each strategy (NEW FORMAT) */
export interface IDistanceSaving {
  savedKm: number;
  savedPercentage: number;
  isShortest: boolean;
}

/** Route comparison metrics - supports both legacy and new comparison formats */
export interface IRoutesComparison {
  // ========== NEW FORMAT (when comparisonEnabled = true) ==========
  comparisonEnabled?: boolean;

  /** All 3 strategies with their results */
  strategies?: Record<string, ITripStrategy>;

  /** The shortest strategy info */
  shortestStrategy?: {
    id: number;
    key: string;
  };

  /** The active (currently used) strategy */
  activeStrategy?: {
    id: number;
    key: string;
    isOptimal: boolean;
  };

  /** Strategies ranked by distance (shortest first) */
  ranking?: IStrategyRanking[];

  /** Distance savings for each strategy compared to shortest */
  distanceSavings?: Record<string, IDistanceSaving>;

  // ========== LEGACY FORMAT (when comparisonEnabled = false or undefined) ==========
  /** Route Optimization result - المسار المحسّن */
  originalRoute?: IComparisonRoute;
  /** Hybrid Shortest result - المسار الأقصر المختلط */
  shortestRoute?: IComparisonRoute;
  difference?: {
    distanceSavedKm: number;
    percentageSaved: number;
  };
}

/** Last calculation details */
export interface ILastCalculation {
  calculated_at: string;
  trigger: string;
  strategy_used: number;
}

/** API calls statistics */
export interface IApiCallStats {
  total_calculations: number;
  total_route_optimization_calls: number;
  total_routes_api_calls: number;
  total_api_calls: number;
}

/** Distance summary for payment calculation */
export interface IDistanceSummary {
  /** المسافة بدون عودة */
  withoutReturn: number;
  /** المسافة مع عودة */
  withReturn: number;
  /** مسافة العودة فقط */
  returnSegmentOnly: number;
  /** إعداد الرحلة - هل يشمل العودة */
  paymentIncludesReturn: boolean;
  /** المسافة المعتمدة للدفع */
  activeForPayment: number;
}

/** Route statistics */
export interface IRouteStats {
  calculationCount: number;
  apiCalls?: IApiCallStats;
  lastCalculation?: ILastCalculation;
  calculationHistory?: any[];
  /** Distance breakdown summary */
  distanceSummary?: IDistanceSummary;
}

/** Route strategy enum */
export enum RouteStrategy {
  ROUTE_OPTIMIZATION = 1,
  HYBRID_SHORTEST = 2,
  NEAREST_NEIGHBOR_SHORTEST = 3,
}

export interface ITrip extends IBaseModel {
  id: number;
  number?: string;
  driverId: number;
  driverClassificationId?: number;
  storeId?: number;
  status: TripStatus;

  // Metrics
  ordersCount?: number;
  storesCount?: number;
  deliveryPointsCount?: number;
  completedOrdersCount?: number;
  totalDistance?: number;
  /** Total distance including return trip to driver's store (km) */
  totalDistanceWithReturn?: number | null;
  totalCost?: number;
  /** Total cost including return trip */
  totalCostWithReturn?: number | null;
  /** The distance used for payment calculation (based on includesReturnTrip setting) */
  distanceForPayment?: number;
  /** The cost used for payment calculation (based on includesReturnTrip setting) */
  costForPayment?: number;
  /** Whether this trip includes return trip in payment calculation */
  includesReturnTrip?: boolean;
  baseCost?: number;
  bonusCost?: number;
  totalOrderPrices?: number;

  // Duration
  estimatedDurationMinutes?: number;
  /** Estimated duration including return trip to driver's store (minutes) */
  estimatedDurationWithReturnMinutes?: number | null;
  /** The duration used for payment calculation (based on includesReturnTrip setting) */
  durationForPayment?: number;
  /** Actual duration for completed trips only (minutes) */
  actualDurationMinutes?: number | null;

  // Payment
  paymentStatus?: PaymentStatus;
  paymentReceivedById?: number | null; // ID فقط
  paymentReceivedBy?: {
    // بيانات المستخدم الكاملة (TripDetails فقط)
    id: number;
    firstName: string;
    lastName: string;
  } | null;
  paymentReceivedAt?: string | null;

  // Start-check audit - توثيق تأكيد المنتجات المحفوظة خارج السلة قبل البدء
  startCheckConfirmedById?: number | null;
  startCheckConfirmedBy?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    fullName?: string | null;
  } | null;
  startCheckConfirmedAt?: string | null;
  /** Present only when the request uses include=startCheck. */
  startCheck?: ITripStartCheck;

  // Payment Received Tracking - مجموع الطلبات النقدية والمبلغ الفعلي المستلم
  expectedCollectedFromClients?: number | null; // مجموع الطلبات النقدية (CASH) - ما يجب جمعه من العملاء
  actualAmountReceived?: number | null; // المبلغ الفعلي المستلم من السائق

  // Route info
  routeInfo?: any;
  costBreakdown?: ICostBreakdown;

  // New Route System Fields (replaces polyline, transitionPolylines, polylineSource)
  /** New polylines structure with segments for map rendering */
  mapPolylines?: IMapPolylines | null;
  /** Active route with ordered route points */
  activeRoute?: IActiveRoute | null;
  /** Route comparison data (only for hybrid strategy) */
  routesComparison?: IRoutesComparison | null;
  /** Strategy used for route calculation (1=ROUTE_OPTIMIZATION, 2=HYBRID_SHORTEST, 3=NEAREST_NEIGHBOR_SHORTEST) */
  strategyUsed?: number | null;
  /** Strategy name string */
  strategyName?: string | null;
  /** Route calculation statistics */
  routeStats?: IRouteStats | null;

  // Timestamps
  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;

  // Classification snapshot fields
  calculatingDeliveryCostPerKilometer?: boolean;
  orderDeliveryCost?: number;
  extraOrderFixedBonusEnabled?: boolean;
  deliveryCostPerKilometer?: number;
  extraOrderFixedBonusAmount?: number;

  // Relationships
  driver?: {
    id: number;
    firstName?: string;
    lastName?: string;
    name?: string;
    phone?: string;
    mobile?: string;
    image?: string;
    user?: {
      firstName?: string;
      lastName?: string;
    };
    store?: any;
    driverClassification?: {
      id: number;
      name?: string;
    };
  };
  store?: any;
  stores?: ITripStore[];
  driverClassification?: any;
  orders?: ITripOrder[];
  /** Status change history for this trip */
  statusHistory?: ITripStatusHistory[];
}

export interface ITripResponse {
  data: ITrip[];
  totalCount: number;
}

export interface ITripDetailsResponse {
  data: ITrip;
}

// Helper functions
export const getTripStatusLabel = (status: TripStatus): string => {
  switch (status) {
    case TripStatus.PENDING:
      return "pending";
    case TripStatus.ACTIVE:
      return "active";
    case TripStatus.COMPLETED:
      return "completed";
    case TripStatus.CANCELLED:
      return "cancelled";
    default:
      return "unknown";
  }
};

export const getTripStatusColor = (status: TripStatus): string => {
  switch (status) {
    case TripStatus.PENDING:
      return "gold";
    case TripStatus.ACTIVE:
      return "green";
    case TripStatus.COMPLETED:
      return "blue";
    case TripStatus.CANCELLED:
      return "red";
    default:
      return "default";
  }
};

export const getPaymentStatusLabel = (status: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.ACCEPTED:
      return "payment_accepted";
    case PaymentStatus.PENDING:
      return "payment_pending";
    case PaymentStatus.REJECTED:
      return "payment_rejected";
    default:
      return "unknown";
  }
};

export const getPaymentStatusColor = (status: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.ACCEPTED:
      return "green";
    case PaymentStatus.PENDING:
      return "gold";
    case PaymentStatus.REJECTED:
      return "red";
    default:
      return "default";
  }
};

/** Get strategy display label translation key */
export const getStrategyLabel = (
  strategyName: string | null | undefined,
): string => {
  if (!strategyName) return "strategy_unknown";
  const labels: Record<string, string> = {
    ROUTE_OPTIMIZATION: "strategy_optimized",
    HYBRID_SHORTEST: "strategy_hybrid_shortest",
    NEAREST_NEIGHBOR_SHORTEST: "strategy_nearest_neighbor",
  };
  return labels[strategyName] || "strategy_unknown";
};

/** Get strategy badge color */
export const getStrategyColor = (
  strategyName: string | null | undefined,
): string => {
  if (!strategyName) return "default";
  const colors: Record<string, string> = {
    ROUTE_OPTIMIZATION: "orange",
    HYBRID_SHORTEST: "purple",
    NEAREST_NEIGHBOR_SHORTEST: "gold",
  };
  return colors[strategyName] || "default";
};

/** Format duration from seconds to readable string */
export const formatDurationFromSeconds = (seconds: number): string => {
  if (!seconds || seconds <= 0) return "0";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m ${remainingSeconds}s`;
};

/**
 * Convert GeoJSON location to {lat, lng} format
 * ⚠️ GeoJSON coordinates are [longitude, latitude] NOT [lat, lng]!
 */
export const geoJsonToLatLng = (
  geoJson: IGeoJsonLocation | { lat: number; lng: number } | null | undefined,
): { lat: number; lng: number } | null => {
  if (!geoJson) return null;

  // Handle GeoJSON format
  if ("coordinates" in geoJson && Array.isArray(geoJson.coordinates)) {
    return {
      lat: geoJson.coordinates[1], // latitude is second
      lng: geoJson.coordinates[0], // longitude is first
    };
  }

  // Handle already converted {lat, lng} format for backwards compatibility
  if ("lat" in geoJson && "lng" in geoJson) {
    return { lat: geoJson.lat, lng: geoJson.lng };
  }

  return null;
};

// Confirm trip payment request
export interface IConfirmTripPaymentRequest {
  amount_received: number;
  note?: string;
}

// Confirm trip payment response
export interface IConfirmTripPaymentResponse {
  message: string;
  matched: boolean;
  expected_amount: number;
  amount_received: number;
  difference: number;
  payment_status: number;
  trip: ITrip;
}
